import BaseService from 'services/BaseService';
import DataHelpers from 'helpers/DataHelpers';
import moment from 'moment';

//-------------------------------------------------------------------------------------------------------------------

export default class InstructorService
    extends BaseService {
    
    static async list(param) {
        if (param != null) {
            param = { ...param };
            param.courseIDs = DataHelpers.convertIDList(param.courseIDs);
            param.geoIDs = DataHelpers.convertIDList(param.geoIDs);
            param.instructorIDs = null;
            param.languageIDs = DataHelpers.convertIDList(param.languageIDs);
            param.cityIDs = DataHelpers.convertIDList(param.cityIDs);
            param.activeStatuses = DataHelpers.convertIDList(param.activeStatuses);
        }
        return BaseService.callAPI('instructor/list', param);
    }
    
    static async get(slug) {
        return BaseService.callAPI('instructor/get/' + slug);
    }

    static async listBookingYears(id) {
        return BaseService.callAPI('instructor/list-booking-years/' + id);
    }

    static async listBookings(id, type, year) {
        const param = {
            instructorID: id,
            includeNonATP: true,
            includeATP: true
        };
        if (type == 'future') {
            param.dateFrom = moment().format('YYYY-MM-DD');
        } else {
            param.dateFrom = moment(year + '-01-01').format('YYYY-MM-DD');
            param.dateTo = moment.min(moment(year + '-12-31'), moment().startOf('day').add(-1, 'day')).format('YYYY-MM-DD');
        }
        return BaseService.callAPI('booking/list', param);
    }

    static async listAvailableDates(id) {
        return BaseService.callAPI('instructor/list-available-dates/' + id);
    }

    static async getCalendar(param) {
        if (param != null) {
            param = { ...param };
            param.geoIDs = DataHelpers.convertIDList(param.geoIDs);
        }
        return BaseService.callAPI('instructor/get-calendar', param);
    }

    static async getCalendarExcel(param) {
        if (param != null) {
            param = { ...param };
            param.geoIDs = DataHelpers.convertIDList(param.geoIDs);
        }
        return BaseService.callAPI('instructor/get-calendar-excel', {
            ...param,
            isDownload: true,
            fileName: 'Calendar.xlsx'
        });
    }

    static async getEnablement(param) {
        if (param != null) {
            param = { ...param };
            param.courseIDs = DataHelpers.convertIDList(param.courseIDs);
            param.instructorIDs = DataHelpers.convertIDList(param.instructorIDs);
            param.geoIDs = DataHelpers.convertIDList(param.geoIDs);
            param.languageIDs = DataHelpers.convertIDList(param.languageIDs);
            param.countryIDs = DataHelpers.convertIDList(param.countryIDs);
            param.statuses = DataHelpers.convertIDList(param.statuses);
            param.activeStatuses = DataHelpers.convertIDList(param.activeStatuses);
            param.priorities = DataHelpers.convertIDList(param.priorities).map(p => (p == 'true') /* Convert to bool */);
        }
        return BaseService.callAPI('instructor/get-enablement', param);
    }

    static async getEnablementExcel(param) {
        if (param != null) {
            param = { ...param };
            param.courseIDs = DataHelpers.convertIDList(param.courseIDs);
            param.instructorIDs = DataHelpers.convertIDList(param.instructorIDs);
            param.geoIDs = DataHelpers.convertIDList(param.geoIDs);
            param.languageIDs = DataHelpers.convertIDList(param.languageIDs);
            param.countryIDs = DataHelpers.convertIDList(param.countryIDs);
            param.statuses = DataHelpers.convertIDList(param.statuses);
            param.activeStatuses = DataHelpers.convertIDList(param.activeStatuses);
            param.priorities = DataHelpers.convertIDList(param.priorities).map(p => (p == 'true') /* Convert to bool */);
        }
        return BaseService.callAPI('instructor/get-enablement-excel', {
            ...param,
            isDownload: true,
            fileName: 'Enablement.xlsx'
        });
    }

    static async getFullAvailability(slug, param) {
        return BaseService.callAPI('instructor/get-full-availability/' + (slug || ''), param);
    }

    static async updateAvailability(slug, datesAdded, datesRemoved, notes, removeDatesReason) {
        return BaseService.callAPI('instructor/update-availability/' + slug, {
            datesAdded, datesRemoved, notes, removeDatesReason
        });
    }

    static async updateAvailabilityMode(slug, isAvailByDefault) {
        return BaseService.callAPI('instructor/update-availability-mode/' + slug, {
            isAvailByDefault
        });
    }

    static async updateField(slug, field, value) {
        return BaseService.callAPI('instructor/update-field/' + slug, {
            field,
            value
        });
    }
    
    static async save(instructor) {
        return BaseService.callAPI('instructor/save', instructor);
    }

    static async delete(id) {
        return BaseService.callAPI('instructor/delete/' + id);
    }

    static async uploadPhoto(id, file) {
        return BaseService.postFile('instructor/upload-photo/' + id, file);
    }

    static async getRampingInfo(instructorID, courseID) {
        return BaseService.callAPI('instructor/get-ramping-info/' + instructorID + '/' + courseID);
    }

    static async updateRampingInfo(instructorID, instructorCourse) {
        return BaseService.callAPI('instructor/update-ramping-info/' + instructorID, instructorCourse);
    }

    static async listReferralCodes(instructorID) {
        return BaseService.callAPI('instructor/list-referral-codes/' + (instructorID || ''));
    }
    
    static async saveReferralCode(referralCode) {
        return BaseService.callAPI('instructor/save-referral-code', referralCode);
    }

    static async setSpecialAvailabilityType(instructorID, date, specialAvailabilityType, notes) {
        return BaseService.callAPI('instructor/set-special-availability-type', {
            instructorID,
            date,
            specialAvailabilityType,
            notes
        });
    }

    static async submitCourseForApproval(instructorID, courseID) {
        return BaseService.callAPI('instructor/submit-course-for-approval/' + instructorID + '/' + courseID);
    }

    static async getBookingNotes(instructorID) {
        return BaseService.callAPI('instructor/get-booking-notes/' + instructorID);
    }

    static async saveBookingNotes(id, bookingNotes) {
        return BaseService.callAPI('instructor/save-booking-notes', {
            id,
            bookingNotes
        });
    }

    static async getEmailSettings({ instructorID, courseID, certID }) {
        return BaseService.callAPI('instructor/get-email-settings', { instructorID, courseID, certID });
    }

    static async getAllEmailSettings(instructorID) {
        return BaseService.callAPI('instructor/get-all-email-settings/' + instructorID);
    }

    static async saveEmailSettings({ instructorID, courseID, certID, settings }) {
        return BaseService.callAPI('instructor/save-email-settings', { instructorID, courseID, certID, settings });
    }

    static groupByCity(instructors) {
        const cities = {}
        const citiesList = [];
        instructors.forEach(i => {
            if (!i.city || !i.city.lat || !i.city.lng) {
                return;
            }
            if (!cities[i.city.id]) {
                const city = {
                    ...i.city,
                    instructors: []
                };
                cities[i.city.id] = city;
                citiesList.push(city);
            }
            cities[i.city.id].instructors.push({ ...i });
        });
        return citiesList;
    }
}
